import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { tutorialDialogs, type TutorialDialogType } from './config';
import {
  TutorialContext,
  type TutorialContextValue,
  type TutorialDialogOverrides,
} from './context';
import { TutorialDialog } from './TutorialDialog';
import { TutorialHooks } from './TutorialHooks';

const Tutorial: FC<PropsWithChildren> = ({ children }) => {
  const [activeDialog, setActiveDialog] = useState<
    TutorialDialogType | undefined
  >(undefined);
  const overridesRef = useRef<TutorialDialogOverrides | undefined>();

  const openDialog = useCallback(
    (dialog: TutorialDialogType, overrides?: TutorialDialogOverrides): void => {
      overridesRef.current = overrides;
      setActiveDialog(dialog);
    },
    [],
  );

  const closeDialog = useCallback((): void => {
    setActiveDialog((current) => {
      if (current) {
        tutorialDialogs[current].callback?.();
        overridesRef.current?.callback?.();
      }

      overridesRef.current = undefined;

      return undefined;
    });
  }, []);

  const value = useMemo<TutorialContextValue>(
    () => ({ activeDialog, setActiveDialog, openDialog, closeDialog }),
    [activeDialog, openDialog, closeDialog],
  );

  const activeConfig = activeDialog ? tutorialDialogs[activeDialog] : null;

  return (
    <TutorialContext.Provider value={value}>
      <TutorialHooks />
      {!!activeConfig && (
        <TutorialDialog config={activeConfig} onClose={closeDialog} />
      )}
      {children}
    </TutorialContext.Provider>
  );
};

export { Tutorial, TutorialContext };
